import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { SearchHeader } from '@ifca-root/react-component/src/components/Header/SearchHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import {
	AppBar,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Tab,
	Tabs,
	TextField,
	Toolbar,
} from '@material-ui/core';
import { Tune, CloudDownload } from '@material-ui/icons';
import AppContext from 'containers/App/Store/AppContext';
import { useGetOutletQuery, useHasOpeningBalanceImportedQuery } from 'generated/graphql';
import React, { useContext, useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { startOfDay, endOfDay, format } from 'date-fns';
import { StockBalanceTab } from './Tabs/StockBalanceTab';
import { StockTransactionTab } from './Tabs/StockTransactionTab';
import { SoldOutTab } from './Tabs/SoldOutTab';
import FloatButton from '@ifca-root/react-component/src/components/Button/FloatButton';
import { StockMonitoringFilterDialog } from './FilterDialog';
import { ImportExportDialog } from './ImportExportDialog';
import PublishIcon from '@material-ui/icons/Publish';
function a11yProps(index: any) {
	return {
		id: `stock-tab-${index}`,
		'aria-controls': `stock-tabpanel-${index}`,
	};
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: any;
	value: any;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`stock-tabpanel-${index}`}
			aria-labelledby={`stock-tab-${index}`}
			style={{ padding: '0px' }}
			{...other}
		>
			{value === index && <>{children}</>}
		</div>
	);
}

export const StockMonitoringList = () => {
	const { outletID }: any = useParams();
	const { globalState, dispatch }: any = useContext(AppContext as any);
	const history = useHistory();
	const location = useLocation();

	// Get tab from URL query parameter, default to 0 (Stock Balance)
	const getInitialTab = () => {
		const searchParams = new URLSearchParams(location.search);
		const tabParam = searchParams.get('tab');
			if (tabParam !== null) {
				const tab = parseInt(tabParam, 10);
				if (!isNaN(tab) && tab >= 0 && tab <= 2) {
					return tab;
				}
			}
		return 0;
	};

	const [tabValue, setTabValue] = useState(getInitialTab());

	// Update tab when URL query parameter changes
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const tabParam = searchParams.get('tab');
			if (tabParam !== null) {
				const tab = parseInt(tabParam, 10);
				if (!isNaN(tab) && tab >= 0 && tab <= 2) {
					setTabValue(tab);
				}
			}
	}, [location.search]);

	// Default to today's date (start and end of day)
	const getTodayStartDate = () => {
		const today = new Date();
		return startOfDay(today);
	};

	const getTodayEndDate = () => {
		const today = new Date();
		return endOfDay(today);
	};

	const [startDate, setStartDate] = useState<Date | null>(getTodayStartDate());
	const [endDate, setEndDate] = useState<Date | null>(getTodayEndDate());
	const [searchQuery, setSearchQuery] = useState('');
	const [openFilterDialog, setOpenFilterDialog] = useState(false);
	const [openImportExportDialog, setOpenImportExportDialog] = useState(false);

	// Check if opening balance has already been imported
	const { data: importStatusData } = useHasOpeningBalanceImportedQuery({
		variables: { outletID },
		fetchPolicy: 'cache-and-network',
	});

	const hasImported = importStatusData?.hasOpeningBalanceImported || false;
	
	// Helper functions for filter dialog (returns YYYY-MM-DD strings)
	const getTodayDate = () => {
		const today = new Date();
		return format(startOfDay(today), 'yyyy-MM-dd');
	};

	const getTodayEndDateISO = () => {
		const today = new Date();
		return format(endOfDay(today), 'yyyy-MM-dd');
	};

	const [filterValue, setFilterValue] = useState<any>({
		startDate: getTodayDate(),
		endDate: getTodayEndDateISO(),
		itemName: '',
		category: '',
	});

	const initialFilterValue = {
		startDate: getTodayDate(),
		endDate: getTodayEndDateISO(),
		itemName: '',
		category: '',
	};

	// Debug log
	useEffect(() => {
		console.log('[StockMonitoring] Component mounted', { outletID });
	}, [outletID]);

	const {
		called: outletCalled,
		loading: outletLoading,
		data: { getOutlet } = { getOutlet: [] },
	} = useGetOutletQuery({
		fetchPolicy: 'network-only',
		variables: { ID: outletID },
	});

	const handleDrawer = () => {
		dispatch({
			type: 'drawerOpen',
			payload: !globalState.drawerOpen,
		});
	};

	const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
		setTabValue(newValue);
		// Remove tab query parameter from URL when user manually changes tabs
		history.replace(`/menu/outlet-app/${outletID}/stock-monitoring`);
	};

	const handleDateChange = (direction: 'prev' | 'next') => {
		const daysToAdd = direction === 'next' ? 1 : -1;
		const newStartDate = new Date(startDate || new Date());
		newStartDate.setDate(newStartDate.getDate() + daysToAdd);
		setStartDate(newStartDate);
		
		const newEndDate = new Date(endDate || new Date());
		newEndDate.setDate(newEndDate.getDate() + daysToAdd);
		setEndDate(newEndDate);
	};

	const triggerFilterDialogOpen = () => {
		setOpenFilterDialog(true);
		const savedFilter = JSON.parse(localStorage.getItem('stockMonitoringFilterArgs') || 'null');
		if (savedFilter) {
			setFilterValue({
				startDate: savedFilter.startDate || getTodayDate(),
				endDate: savedFilter.endDate || getTodayEndDate(),
				itemName: savedFilter.itemName || '',
				category: savedFilter.category || '',
			});
		} else {
			// Set default to today if no saved filter
			setFilterValue({
				startDate: getTodayDate(),
				endDate: getTodayEndDate(),
				itemName: '',
				category: '',
			});
		}
	};

	const triggerFilterDialogClose = () => {
		setOpenFilterDialog(false);
	};

	const handleFilterApply = (filter: any) => {
		// Apply filter logic here
		// This will be called when user confirms the filter
		console.log('Filter applied:', filter);
		// You can update the date range or trigger data refetch here
		if (filter.startDate) {
			setStartDate(new Date(filter.startDate));
		}
		if (filter.endDate) {
			setEndDate(new Date(filter.endDate));
		}
	};

	return (
		<>
			{outletLoading && <Loading />}
			<MainHeader
				onClick={handleDrawer}
				mainBtn="menu"
				smTitle="Outlet App"
				title={getOutlet[0]?.name || 'Loading...'}
				routeSegments={[
					{ name: 'Stock Monitoring', current: true },
				]}
			/>

		

		
			<SearchHeader
				title="Stock Monitoring"
				value={searchQuery}
				onChangeAction={(e) => {
					setSearchQuery(e?.target?.value || '');
				}}
				fixed
				search
				onCloseAction={() => setSearchQuery('')}
				options={[
					{
						icon: <PublishIcon />,
						onClick: () => {
							setOpenImportExportDialog(true);
						},
					},
					{
						icon: <Tune />,
						onClick: () => {
							triggerFilterDialogOpen();
						},
					},
				]}
			/>

	
			<div className="core-tab-bill-listing fixed-search-header-fw">
				<AppBar position="static">
					<Tabs
						aria-label="stock monitoring tabs"
						indicatorColor="primary"
						textColor="primary"
						value={tabValue}
						onChange={handleTabChange}
						variant="fullWidth"
					>
						<Tab 
							label="Stock Balance"
							style={{ textTransform: 'none' }}
							{...a11yProps(0)}
						/>
						<Tab 
							label="Stock Transaction"
							style={{ textTransform: 'none' }}
							{...a11yProps(1)}
						/>
						<Tab 
							label="Sold Out"
							style={{ textTransform: 'none' }}
							{...a11yProps(2)}
						/>
					</Tabs>
				</AppBar>
			</div>

			{/* Tab Content */}
			<ContentWrapper dynamicInfoSearch float >
				<TabPanel value={tabValue} index={0}>
					<StockBalanceTab 
						outletID={outletID}
						startDate={startDate}
						endDate={endDate}
						searchQuery={searchQuery}
					/>
				</TabPanel>
				<TabPanel value={tabValue} index={1}>
					<StockTransactionTab 
						outletID={outletID}
						startDate={startDate}
						endDate={endDate}
						searchQuery={searchQuery}
					/>
                   <FloatButton    
					onClick={() => {
						history.push(`/menu/outlet-app/${outletID}/stock-monitoring/stock-transaction`);
					}}
				/>
				</TabPanel>
				<TabPanel value={tabValue} index={2}>
					<SoldOutTab 
						outletID={outletID}
						startDate={startDate}
						endDate={endDate}
						searchQuery={searchQuery}
					/>
				</TabPanel>
			</ContentWrapper>
			<StockMonitoringFilterDialog
				setFilterValue={setFilterValue}
				filterValue={filterValue}
				initialValue={initialFilterValue}
				onApply={handleFilterApply}
				open={openFilterDialog}
				close={triggerFilterDialogClose}
			/>

			<ImportExportDialog
				open={openImportExportDialog}
				onClose={() => setOpenImportExportDialog(false)}
				outletID={outletID}
				currentTab={tabValue}
			/>
		</>
	);
};

